import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { matchLegacyRedirect } from './matchLegacyRedirect';
import { normalizeLegacyUrl } from './normalizeLegacyUrl';

interface LegacyRedirectBoundaryProps {
  children: ReactNode;
}

export function LegacyRedirectBoundary({ children }: LegacyRedirectBoundaryProps) {
  // During RR v7 prerender (Node SSR), `window` is undefined. The legacy
  // redirect check is a pure client concern — bail out cleanly when there's
  // no DOM and fall through to render children. The browser will re-evaluate
  // on hydration with a real `window`.
  const initialUrlRef = useRef(typeof window !== 'undefined' ? window.location.href : null);
  const redirectMatchRef = useRef(
    initialUrlRef.current ? matchLegacyRedirect(initialUrlRef.current) : null,
  );
  const hasReplacedRef = useRef(false);

  useEffect(() => {
    const redirectMatch = redirectMatchRef.current;
    const initialUrl = initialUrlRef.current;

    if (!redirectMatch || hasReplacedRef.current || !initialUrl) {
      return;
    }

    const normalizedCurrentUrl = normalizeLegacyUrl(initialUrl);
    const normalizedTargetUrl = normalizeLegacyUrl(new URL(redirectMatch.targetPath, initialUrl));

    if (normalizedCurrentUrl.key === normalizedTargetUrl.key) {
      return;
    }

    hasReplacedRef.current = true;
    window.location.replace(redirectMatch.targetPath);
  }, []);

  if (redirectMatchRef.current && initialUrlRef.current) {
    const normalizedCurrentUrl = normalizeLegacyUrl(initialUrlRef.current);
    const normalizedTargetUrl = normalizeLegacyUrl(new URL(redirectMatchRef.current.targetPath, initialUrlRef.current));

    if (normalizedCurrentUrl.key !== normalizedTargetUrl.key) {
      return null;
    }
  }

  return <>{children}</>;
}
